// Scrolling between checkout steps, done frame by frame instead of with the
// browser's smooth scrolling. iOS Safari's native smooth scroll stutters when
// the layout changes underneath it (a step collapsing, the keyboard closing),
// and the site-wide `scroll-behavior: smooth` would smooth every frame again,
// so it is switched off while this runs.

const DURATION = 450;

let frame = null;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// `scroll-behavior: auto` for the duration, so each step lands immediately.
function setScrollTop(top) {
    document.documentElement.style.scrollBehavior = "auto";
    window.scrollTo(0, top);
    if (!frame) {
        document.documentElement.style.scrollBehavior = "";
    }
}

function stop() {
    cancelAnimationFrame(frame);
    frame = null;
    document.documentElement.style.scrollBehavior = "";
}

// A touch or wheel hands control back to the customer.
["touchstart", "wheel"].forEach((type) =>
    window.addEventListener(type, stop, { passive: true }),
);

// Scroll the page by `delta` px with no animation, e.g. to cancel out a layout
// shift above the viewport.
export function jumpBy(delta) {
    if (delta) {
        setScrollTop(window.scrollY + delta);
    }
}

// Close the on-screen keyboard, and resolve once the viewport has settled so
// the scroll isn't thrown off by the page resizing mid-animation.
export function dismissKeyboard() {
    const active = document.activeElement;
    const typing = active?.matches?.("input, textarea, select");
    const viewport = window.visualViewport;
    const keyboardOpen =
        typing && viewport && viewport.height < window.innerHeight - 100;

    if (typing) {
        active.blur();
    }
    if (!keyboardOpen) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const done = () => {
            viewport.removeEventListener("resize", onResize);
            clearTimeout(timer);
            resolve();
        };
        // The keyboard animates closed over a few resize events.
        let timer = setTimeout(done, 400);
        const onResize = () => {
            clearTimeout(timer);
            timer = setTimeout(done, 80);
        };
        viewport.addEventListener("resize", onResize);
    });
}

// Ease the page so `element` sits `offset` px below the top of the viewport.
// The target is re-measured every frame, so late layout changes don't leave the
// scroll short or overshooting.
export function scrollToElement(element, offset) {
    stop();

    const target = () =>
        Math.max(
            0,
            Math.min(
                element.getBoundingClientRect().top + window.scrollY - offset,
                document.documentElement.scrollHeight - window.innerHeight,
            ),
        );

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setScrollTop(target());
        return;
    }

    const start = window.scrollY;
    const startTime = performance.now();

    const step = (now) => {
        const t = Math.min(1, (now - startTime) / DURATION);
        setScrollTop(start + (target() - start) * easeOutCubic(t));
        if (t < 1) {
            frame = requestAnimationFrame(step);
        } else {
            stop();
        }
    };
    frame = requestAnimationFrame(step);
}
